import { Flag, Moon, Sun } from 'lucide-react';
import { Fragment } from 'react';
import type { CalendarItem } from '@tempo/core';
import { MODE_LABEL, dateOf, duration, time } from '../lib/format';
import { useNow, usePlaces } from '../lib/hooks';
import { FlagBadges, ItemIcon } from './kinds';

const GAP_MIN = 20 * 60_000;

export function DayTimeline({ date, items, onSelect }: { date: string; items: CalendarItem[]; onSelect: (i: CalendarItem) => void }) {
  const now = useNow(30_000);
  const places = usePlaces().data ?? [];
  const placeName = (id: string | null | undefined) => (id ? places.find((p) => p.id === id)?.name : null);
  const byId = new Map(items.map((i) => [i.id, i]));

  const wake = items.find((i) => i.kind === 'sleep' && dateOf(i.end) === date && i.start < i.end);
  const bed = items.find((i) => i.kind === 'sleep' && dateOf(i.start) === date && i !== wake);
  const markers = items.filter((i) => i.kind === 'deadline' || i.allDay);
  const timed = items
    .filter((i) => i.kind !== 'sleep' && i.kind !== 'deadline' && !i.allDay && dateOf(i.start) === date)
    .sort((a, b) => a.start - b.start || a.end - b.end);
  const isToday = dateOf(now) === date;
  const nowIdx = isToday ? timed.findIndex((i) => i.start > now) : -1;

  if (!timed.length && !markers.length) {
    return <div className="empty small">Nothing planned – a free day.</div>;
  }

  let lastEnd = wake?.end ?? null;
  return (
    <div className="timeline">
      {markers.map((m) => (
        <button key={m.id} className="tl-item" onClick={() => onSelect(m)}>
          <div className="tl-time">{m.allDay ? 'All day' : time(m.start)}</div>
          <div className="tl-bar" style={{ background: m.color, minHeight: 22 }} />
          <div className="tl-body">
            <div className="tl-title" style={{ color: m.kind === 'deadline' ? m.color : undefined }}>
              {m.kind === 'deadline' && <Flag size={14} />} {m.title}
            </div>
          </div>
        </button>
      ))}
      {wake && (
        <div className="tl-sleep">
          <Sun size={14} /> Up from {time(wake.end)}
        </div>
      )}
      {timed.map((it, idx) => {
        const gap = lastEnd != null && it.start - lastEnd >= GAP_MIN ? it.start - lastEnd : 0;
        const gapStart = lastEnd;
        lastEnd = Math.max(lastEnd ?? 0, it.end);
        const past = it.end < now;
        const target = it.targetId ? byId.get(it.targetId) : null;
        return (
          <Fragment key={it.id}>
            {idx === nowIdx && (
              <div className="tl-now">
                <span>{time(now)}</span>
              </div>
            )}
            {gap > 0 && !(idx === nowIdx && gapStart! < now && it.start - now < GAP_MIN) && (
              <div className="tl-gap">
                <span />
                <div className="line" />
                <span>Free · {duration(gap)}</span>
              </div>
            )}
            {it.kind === 'travel' ? (
              <button className={`tl-item travel ${past ? 'past' : ''}`} onClick={() => onSelect(it)}>
                <div className="tl-time" style={{ fontSize: 13, color: 'var(--text-2)' }}>
                  {time(it.start)}
                </div>
                <div className="tl-bar" style={{ background: it.color }} />
                <div className="tl-body">
                  <div className="tl-title">
                    <span className="ico">
                      <ItemIcon item={it} size={15} />
                    </span>
                    <span className="txt">
                      {target ? `Leave for ${target.title}` : it.title} · {duration(it.end - it.start)} {MODE_LABEL[it.mode ?? ''] ?? ''} <FlagBadges item={it} />
                    </span>
                  </div>
                </div>
              </button>
            ) : (
              <button className={`tl-item ${past ? 'past' : ''}`} onClick={() => onSelect(it)}>
                <div className="tl-time">
                  {time(it.start)}
                  <span className="end">{time(it.end)}</span>
                </div>
                <div className="tl-bar" style={{ background: it.color }} />
                <div className="tl-body">
                  <div className="tl-title">
                    <span className="ico" style={{ color: it.color }}>
                      <ItemIcon item={it} size={15} />
                    </span>
                    <span className="txt">{it.title}</span>
                  </div>
                  <div className="tl-meta">
                    <span>{duration(it.end - it.start)}</span>
                    {(placeName(it.placeId) || it.location) && <span>· {placeName(it.placeId) ?? it.location}</span>}
                    <FlagBadges item={it} />
                  </div>
                </div>
              </button>
            )}
          </Fragment>
        );
      })}
      {isToday && nowIdx === -1 && timed.length > 0 && (
        <div className="tl-now">
          <span>{time(now)}</span>
        </div>
      )}
      {bed && (
        <div className="tl-sleep">
          <Moon size={14} /> Bed {time(bed.start)}
        </div>
      )}
    </div>
  );
}
